import { 
  Box, 
  Container, 
  Grid, 
  Paper, 
  Stack, 
  Typography, 
  Fade,
  useTheme,
  useMediaQuery,
  Divider,
  Chip
} from "@mui/material";
import { 
  Security, 
  Speed, 
  CloudDone, 
  Verified 
} from "@mui/icons-material";
import loginBg from "../../assets/loginbg.jpg";
import { useLogin } from "./hooks/useLogin";
import { 
  MAIN_CONTAINER_STYLES, 
  LEFT_PANEL_STYLES, 
  RIGHT_PANEL_STYLES,
  LOGO_CONTAINER_STYLES 
} from "./constants";
import ErrorMessage from "./components/ErrorMessage";
import LoginForm from "./components/LoginForm";

/**
 * Componente principal de Login
 * Pantalla de autenticación moderna con diseño split-screen profesional
 */
export default function Login() {
  const { form, loading, error, handleChange, handleLogin } = useLogin();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={MAIN_CONTAINER_STYLES(loginBg)}>
      <Container maxWidth="xl" sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Grid container spacing={0} sx={{ height: { xs: "auto", md: "85vh" }, maxHeight: "900px" }}>
          
          {/* Panel Izquierdo - Branding e Información */}
          {!isMobile && (
            <Grid item xs={12} md={7}>
              <Fade in timeout={800}>
                <Paper elevation={0} sx={LEFT_PANEL_STYLES}>
                  <Stack spacing={5} sx={{ height: "100%", justifyContent: "center" }}>
                    
                    {/* Logo y Título Principal */}
                    <Box sx={LOGO_CONTAINER_STYLES}>
                      <Box
                        component="img"
                        src="/logo.png"
                        alt="Logo Empresa"
                        sx={{
                          width: { xs: 140, md: 180 },
                          height: "auto",
                          mb: 3,
                          filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.2))",
                        }}
                      />
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 800, 
                          color: "#fff",
                          mb: 2,
                          textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                          letterSpacing: "-0.5px"
                        }}
                      >
                        Bienvenido de vuelta
                      </Typography>
                    </Box>

                    <Divider sx={{ borderColor: "rgba(255,255,255,0.15)", mx: 0 }} />

                    {/* Footer Badge */}
                    <Box sx={{ mt: "auto", pt: 4 }}>
                      <Chip 
                        label="Conexión segura" 
                        sx={{ 
                          backgroundColor: "rgba(76, 175, 80, 0.15)",
                          color: "#4caf50",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          border: "1px solid rgba(76, 175, 80, 0.3)"
                        }}
                      />
                    </Box>

                  </Stack>
                </Paper>
              </Fade>
            </Grid>
          )}

          {/* Panel Derecho - Formulario de Login */}
          <Grid item xs={12} md={5}>
            <Fade in timeout={600}>
              <Paper elevation={24} sx={RIGHT_PANEL_STYLES}>
                <Stack spacing={4} sx={{ height: "100%" }}>
                  
                  {/* Header Mobile (Logo) */}
                  {isMobile && (
                    <Box sx={{ textAlign: "center", pt: 2 }}>
                      <Box
                        component="img"
                        src="/logo.png"
                        alt="Logo"
                        sx={{ width: 100, height: "auto", mb: 2 }}
                      />
                    </Box>
                  )}

                  {/* Título del Formulario */}
                  <Box>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700, 
                        color: "#fff",
                        mb: 1,
                        letterSpacing: "-0.5px"
                      }}
                    >
                      Iniciar sesión
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: "rgba(255,255,255,0.6)",
                        fontSize: "0.95rem"
                      }}
                    >
                      Ingresa tus credenciales para continuar
                    </Typography>
                  </Box>

                  {/* Mensaje de Error */}
                  <ErrorMessage error={error} />

                  {/* Formulario */}
                  <Box sx={{ flex: 1 }}>
                    <LoginForm
                      form={form}
                      loading={loading}
                      handleChange={handleChange}
                      handleLogin={handleLogin}
                    />
                  </Box>

                  {/* Footer del Formulario */}
                  <Box sx={{ pt: 2, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: "rgba(255,255,255,0.4)",
                        display: "block",
                        textAlign: "center",
                        fontSize: "0.75rem"
                      }}
                    >
                      © 2025 BreMar. Todos los derechos reservados.
                    </Typography>
                  </Box>

                </Stack>
              </Paper>
            </Fade>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
}

